import { useEffect, useState } from "react";
import {
  BookOpenIcon,
  ArrowUpRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { addToDo, getAllToDo } from "../../apis/HandleApi";
import Navbar from "../../Components/Navbar/Navbar";

const safeLink = (value) =>
  /^https?:\/\//i.test(value || "") ? value : undefined;

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  useEffect(() => {
    let active = true;
    setStatus("loading");
    getAllToDo()
      .then((data) => {
        if (active) {
          setNotes(data);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  const save = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setSaving(true);
    setFeedback("");
    try {
      const { data: note } = await addToDo(
        ...["name", "desc", "linkcourse", "lnknotes"].map((key) =>
          data.get(key).trim(),
        ),
      );
      setNotes((previous) => [note, ...previous]);
      form.reset();
      setFeedback("Notes shared. Thanks for helping someone learn!");
    } catch {
      setFeedback(
        "We couldn’t save your notes. Your entries are still here — please try again.",
      );
    } finally {
      setSaving(false);
    }
  };
  const visible = notes.filter((note) =>
    `${note.name} ${note.desc}`
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <Navbar />
      <main className="page-width page-main" id="main-content">
        <div className="page-heading">
          <p className="eyebrow">A LITTLE KNOWLEDGE, SHARED</p>
          <h1>Good notes. Great company.</h1>
          <p>
            A library built by learners, for learners. Find a fresh perspective
            or share your own.
          </p>
        </div>
        <div className="notes-layout">
          <section className="notes-collection" aria-label="Community notes">
            <div className="search-field">
              <input
                aria-label="Search community notes"
                placeholder="Find notes on a topic…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            {status === "loading" ? (
              <div className="empty-state surface" role="status">
                <span className="loading-ring" />
                <p>Opening the library…</p>
              </div>
            ) : status === "error" ? (
              <div className="empty-state surface" role="alert">
                <h3>The library couldn’t load.</h3>
                <p>Please try again in a moment.</p>
                <button
                  className="button button-secondary"
                  onClick={() => setAttempt(attempt + 1)}
                >
                  Try again
                </button>
              </div>
            ) : visible.length ? (
              <div className="note-grid">
                {visible.map((note) => (
                  <article
                    className="surface note-card"
                    key={note._id || note.name}
                  >
                    <BookOpenIcon />
                    <h3>{note.name}</h3>
                    <p>{note.desc}</p>
                    <div className="note-links">
                      {safeLink(note.lnknotes) && (
                        <a
                          href={note.lnknotes}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Read notes <ArrowUpRightIcon />
                        </a>
                      )}
                      {safeLink(note.linkcourse) && (
                        <a
                          href={note.linkcourse}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View course <ArrowUpRightIcon />
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state surface">
                <BookOpenIcon />
                <h3>
                  {query
                    ? "No notes found just yet."
                    : "Be the first to share."}
                </h3>
                <p>
                  {query
                    ? "Try another topic or a shorter search."
                    : "Your notes could be someone else’s lightbulb moment."}
                </p>
              </div>
            )}
          </section>
          <form className="surface contribute-form" onSubmit={save}>
            <PlusIcon />
            <h2>Pass it on.</h2>
            <p>Share the notes that helped things click.</p>
            <label>
              Title
              <input
                name="name"
                placeholder="e.g. A friendly guide to arrays"
                required
                maxLength={150}
                pattern=".*\S.*"
              />
            </label>
            <label>
              A little context
              <textarea
                name="desc"
                placeholder="What will someone learn from these notes?"
                required
                maxLength={1000}
              />
            </label>
            <label>
              Course link
              <input
                name="linkcourse"
                type="url"
                pattern="https?://.+"
                placeholder="https://youtube.com/playlist?list=…"
                required
              />
            </label>
            <label>
              Notes link
              <input
                name="lnknotes"
                type="url"
                pattern="https?://.+"
                placeholder="https://docs.google.com/…"
                required
              />
            </label>
            <button className="button button-primary" disabled={saving}>
              {saving ? "Sharing…" : "Share your notes"}
              <ArrowUpRightIcon />
            </button>
            {feedback && (
              <p className="form-feedback" role="status">
                {feedback}
              </p>
            )}
          </form>
        </div>
      </main>
    </>
  );
}
